<script setup>
import { ref } from 'vue';
import { CheckIcon, ChevronDownIcon } from '@heroicons/vue/solid';
import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions } from '@headlessui/vue';
import { useInfiniteScroll } from '~/composables/infinite-scroll';
import { getApiRoot } from '~/utilities/api';

const props = defineProps({
  modelValue: { type: String, required: true },
  collectionId: { type: String, default: undefined },
  gardenId: { type: String, default: undefined },
  onlyCats: { type: Boolean, default: false },
});
const { modelValue, collectionId, gardenId, onlyCats } = toRefs(props);
const emit = defineEmits(['update:modelValue']);

let token = onlyCats.value ? null : { id: 'xch', name: 'Chia', code: 'XCH' };
if (modelValue.value) {
  token = await $fetch(`${getApiRoot()}/tokens/${modelValue.value}`);
}

const query = ref('');
const selectedToken = ref(token);
const target = ref();
const { items: tokens } = useInfiniteScroll(
  target,
  () =>
    `${getApiRoot()}/tokens?query=${query.value}${collectionId.value ? `&collection_id=${collectionId.value}` : ''}${
      gardenId.value ? `&garden_id=${gardenId.value}` : ''
    }`,
  [query, collectionId, gardenId],
  20
);
const filteredTokens = computed(() => tokens.value.filter((token) => (onlyCats.value ? token.id !== 'xch' : true)));

const onUpdate = () => {
  emit('update:modelValue', selectedToken.value.id);
  query.value = '';
};
</script>

<template>
  <Combobox v-model="selectedToken" as="div" @update:model-value="onUpdate()">
    <div class="relative w-32">
      <ComboboxButton
        v-if="selectedToken"
        class="absolute inset-y-0 left-0 flex items-center rounded-r-md px-2 focus:outline-none"
      >
        <img
          :src="`https://icons.dexie.space/${selectedToken.id}.webp`"
          alt=""
          class="h-6 w-6 flex-shrink-0 rounded-full"
        />
      </ComboboxButton>
      <ComboboxInput
        class="w-full rounded-md border border-neutral-300 bg-white py-2 pl-10 pr-8 text-neutral-900 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 dark:border-neutral-600 dark:bg-neutral-800 dark:text-white sm:text-sm"
        :display-value="(inputToken) => inputToken?.code"
        @change="query = $event.target.value"
      />
      <ComboboxButton class="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
        <ChevronDownIcon class="h-5 w-5 text-neutral-400 dark:text-neutral-500" aria-hidden="true" />
      </ComboboxButton>

      <ComboboxOptions
        v-show="tokens.length > 0"
        class="absolute z-50 mt-2 max-h-56 w-72 overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-neutral-300 focus:outline-none dark:bg-neutral-800 dark:ring-white/5 sm:text-sm"
      >
        <ComboboxOption
          v-for="listTokens in filteredTokens"
          :key="listTokens.id"
          v-slot="{ active, selected }"
          :value="listTokens"
          as="template"
        >
          <li
            :class="[
              'relative cursor-default select-none py-2 pl-3 pr-9',
              active
                ? 'bg-neutral-100 text-neutral-900 dark:bg-neutral-700 dark:text-neutral-100'
                : 'text-neutral-900 dark:text-neutral-100',
            ]"
          >
            <div class="flex items-center">
              <img
                :src="`https://icons.dexie.space/${listTokens.id}.webp`"
                alt=""
                class="h-6 w-6 flex-shrink-0 rounded-full"
              />
              <span :class="['ml-3 truncate', selected && 'font-semibold']">
                {{ listTokens.name }}
              </span>
            </div>

            <span
              v-if="selected"
              :class="['absolute inset-y-0 right-0 flex items-center pr-4', 'text-black dark:text-white']"
            >
              <CheckIcon class="h-5 w-5" aria-hidden="true" />
            </span>
          </li>
        </ComboboxOption>
        <div ref="target" />
      </ComboboxOptions>
    </div>
  </Combobox>
</template>
